import {
  type InfiniteData,
  tsClientOptions,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@zg-rentals/react-query5';
import {
  SatelliteWebLandlordLeadManagementHandler_leadManagementTable,
  SatelliteWebLandlordLeadManagementHandler_leadManagementTable_Path,
} from '@zg-rentals/ts-satellite-client';
import type {
  SatelliteWebLandlordLeadManagementTableRequestDto,
  SatelliteWebLandlordLeadManagementTableResponseDto,
  SatelliteWebLandlordLeadManagementTableRowResponseDto,
} from '@zg-rentals/ts-satellite-client';
import type { SatelliteConversationFolderTypeV2 } from '@zg-rentals/ts-satellite-client';
import type { SatelliteConversationReadRepliedStatusTypeV2 } from '@zg-rentals/ts-satellite-client';
import type { SatelliteConversationRenterProgressStatusTypeV2 } from '@zg-rentals/ts-satellite-client';
import type { SatelliteLeadManagementSourceType } from '@zg-rentals/ts-satellite-client';
import { logError } from '@zg-rentals/log-error';

import { useCallback, useContext, useEffect, useMemo } from 'react';
import { LeadManagementContext } from '../contexts/LeadManagementContextProvider';
import { is4xxError } from '../utils/errorUtils';

type LeadManagementTableQueryParams = {
  clientTimeZone: string;
  conversationId?: string | null;
  folder: SatelliteConversationFolderTypeV2;
  // Listing filter; empty means no filter (all listings).
  listingAliases: Array<string>;
  minCriteriaMatches?: number | null;
  readRepliedStatus: SatelliteConversationReadRepliedStatusTypeV2;
  renterProgressStatus: SatelliteConversationRenterProgressStatusTypeV2;
  // Source filter; null/undefined means no filter (all sources).
  source?: SatelliteLeadManagementSourceType | null;
};

type LeadManagementInfiniteData = InfiniteData<SatelliteWebLandlordLeadManagementTableResponseDto, number>;

export const LEAD_MANAGEMENT_MAX_RETRIES = 2;

/**
 * Retry transient failures (5xx / network) but never a 4xx — a 403 (lead the user doesn't own) /
 * 404 (stale conversation) can't be fixed by retrying, and retrying just multiplies the failure
 * and inflates backend 4xx metrics. Exported as a pure predicate so it's unit-testable without a
 * React Query instance.
 */
export function shouldRetryLeadManagementQuery(failureCount: number, error: unknown): boolean {
  if (is4xxError(error)) {
    return false;
  }
  return failureCount < LEAD_MANAGEMENT_MAX_RETRIES;
}

/** Query key prefix shared across all lead management table queries for targeted invalidation. */
export const LEAD_MANAGEMENT_TABLE_QUERY_KEY = SatelliteWebLandlordLeadManagementHandler_leadManagementTable_Path;

/** Query key prefix for conversation-scoped lead lookups (see useLeadLookupByConversationId). */
export const LEAD_BY_CONVERSATION_LOOKUP_QUERY_KEY = 'satellite:landlord:leadByConversationId';

/**
 * Builds the query key for a lead management table query. Used for cache invalidation.
 * When called without params, returns just the prefix to match all lead management table queries.
 */
export const getLeadManagementTableQueryKey = (params?: LeadManagementTableQueryParams) =>
  params ? ([LEAD_MANAGEMENT_TABLE_QUERY_KEY, params] as const) : ([LEAD_MANAGEMENT_TABLE_QUERY_KEY] as const);

/** Shared infinite query options for the lead management table endpoint. */
function getInfiniteQueryOptions(params: LeadManagementTableQueryParams) {
  return {
    queryKey: getLeadManagementTableQueryKey(params) as readonly [string, LeadManagementTableQueryParams],
    queryFn: ({ pageParam }: { pageParam: number }) => {
      const request: SatelliteWebLandlordLeadManagementTableRequestDto = {
        clientTimeZone: params.clientTimeZone,
        conversationId: params.conversationId || undefined,
        folder: params.folder,
        listingAliases: params.listingAliases,
        minCriteriaMatches: params.minCriteriaMatches ?? undefined,
        readRepliedStatus: params.readRepliedStatus,
        renterProgressStatus: params.renterProgressStatus,
        source: params.source ?? undefined,
        start: pageParam,
      };
      return SatelliteWebLandlordLeadManagementHandler_leadManagementTable(request);
    },
    getNextPageParam: (
      lastPage: SatelliteWebLandlordLeadManagementTableResponseDto,
      allPages: Array<SatelliteWebLandlordLeadManagementTableResponseDto>,
    ) => {
      if (!lastPage.hasNextPage) return undefined;
      const totalLoaded = allPages.reduce((sum, page) => sum + page.leads.length, 0);
      // API rejects start values above 100
      if (totalLoaded > 100) return undefined;
      return totalLoaded;
    },
    initialPageParam: 0,
    staleTime: 30000,
    // Don't retry or window-focus-refetch a 4xx (e.g. a 403 for a lead the user doesn't own);
    // doing so re-hits the endpoint repeatedly and inflates `Http403ForbiddenException`. Without
    // these, React Query's defaults apply (3 retries, refetch on focus — SLW's query provider
    // only sets `staleTime`).
    retry: shouldRetryLeadManagementQuery,
    refetchOnWindowFocus: false,
    meta: {
      tags: ['leadManagement:table'],
      context: {
        api: 'leadManagementTable',
        details: 'useLeadManagementTableQuery - Error fetching lead management table data',
      },
    },
  };
}

/**
 * Hook wrapping the proxit leadManagementTable endpoint with useInfiniteQuery for pagination.
 *
 * All components that need lead management table data should call this hook directly —
 * React Query deduplicates requests sharing the same query key.
 */
export default function useLeadManagementTableQuery(
  params: LeadManagementTableQueryParams,
  { enabled = true }: { enabled?: boolean } = {},
) {
  const queryResult = useInfiniteQuery({
    ...getInfiniteQueryOptions(params),
    enabled,
  });

  const pages = queryResult.data?.pages;

  const { leads, hasNullishLead } = useMemo<{
    leads: Array<SatelliteWebLandlordLeadManagementTableRowResponseDto>;
    hasNullishLead: boolean;
  }>(() => {
    if (!pages) return { leads: [], hasNullishLead: false };
    const flattened = pages.flatMap((page) => page.leads ?? []);
    const filtered = flattened.filter(Boolean);
    return { leads: filtered, hasNullishLead: filtered.length !== flattened.length };
  }, [pages]);

  useEffect(() => {
    if (!hasNullishLead) return;
    logError({
      error: new Error('leadManagementTable response contained nullish lead entries'),
      errorType: 'LeadManagementTableNullishLead',
      context: {
        listingAliases: params.listingAliases.join(',') || null,
        conversationId: params.conversationId ?? null,
      },
    });
    // Log once per occurrence; params intentionally excluded so a listing switch
    // doesn't re-fire while hasNullishLead is still true.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasNullishLead]);

  /** Total lead count from the first page response. */
  const totalLeadCount = queryResult.data?.pages[0]?.totalLeadCount;

  /** Whether there are more pages to load. */
  const hasNextPage = queryResult.hasNextPage ?? false;

  /** Sources the selected listings have leads from; undefined until the first page lands. */
  const availableSources = queryResult.data?.pages[0]?.availableSources;

  return {
    ...queryResult,
    availableSources,
    leads,
    totalLeadCount,
    hasNextPage,
  };
}

/** Hook to get the invalidation function for lead management table queries. */
export function useInvalidateLeadManagementTable() {
  const queryClient = useQueryClient();

  return useCallback(() => {
    queryClient.invalidateQueries({ queryKey: getLeadManagementTableQueryKey() });
    // The conversation-scoped lookup caches under its own key, so invalidate it alongside the
    // table — otherwise a lead resolved via the lookup keeps a stale nextStep/status after an
    // action refreshes the table.
    queryClient.invalidateQueries({ queryKey: [LEAD_BY_CONVERSATION_LOOKUP_QUERY_KEY] });
  }, [queryClient]);
}

/**
 * Optimistically update a single lead in all cached lead management table queries.
 * Returns a rollback function that restores the previous cache state.
 */
export function useOptimisticLeadUpdate() {
  const queryClient = useQueryClient();

  return useCallback(
    (
      conversationId: string,
      updater: (
        lead: SatelliteWebLandlordLeadManagementTableRowResponseDto,
      ) => SatelliteWebLandlordLeadManagementTableRowResponseDto,
    ) => {
      const queryFilter = { queryKey: getLeadManagementTableQueryKey() };
      const previousEntries = queryClient.getQueriesData<LeadManagementInfiniteData>(queryFilter);

      queryClient.setQueriesData<LeadManagementInfiniteData>(queryFilter, (oldData) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          pages: oldData.pages.map((page) => ({
            ...page,
            leads: (page.leads ?? [])
              .filter(Boolean)
              .map((lead) => (lead.conversationId === conversationId ? updater(lead) : lead)),
          })),
        };
      });

      return () => {
        for (const [queryKey, data] of previousEntries) {
          queryClient.setQueryData(queryKey, data);
        }
      };
    },
    [queryClient],
  );
}

/**
 * Optimistically remove a lead from all cached lead management table queries.
 * Returns a rollback function that restores the previous cache state.
 */
export function useOptimisticLeadRemoval() {
  const queryClient = useQueryClient();

  return useCallback(
    (conversationId: string) => {
      const queryFilter = { queryKey: getLeadManagementTableQueryKey() };
      const previousEntries = queryClient.getQueriesData<LeadManagementInfiniteData>(queryFilter);

      queryClient.setQueriesData<LeadManagementInfiniteData>(queryFilter, (oldData) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          pages: oldData.pages.map((page) => ({
            ...page,
            leads: (page.leads ?? []).filter((lead) => lead && lead.conversationId !== conversationId),
          })),
        };
      });

      return () => {
        for (const [queryKey, data] of previousEntries) {
          queryClient.setQueryData(queryKey, data);
        }
      };
    },
    [queryClient],
  );
}

/**
 * Resolve a single lead by conversationId alone — same leadManagementTable endpoint, no page
 * filter params — so the result is independent of the page's filter/selection state. Gate with
 * `enabled` so it only fetches when the lead isn't already in the filter-keyed cache.
 */
export function useLeadLookupByConversationId(
  conversationId: string,
  clientTimeZone: string,
  { enabled = true }: { enabled?: boolean } = {},
): SatelliteWebLandlordLeadManagementTableRowResponseDto | null {
  const postData: SatelliteWebLandlordLeadManagementTableRequestDto = { clientTimeZone, conversationId };

  const { data: lead } = useQuery({
    ...tsClientOptions.queryOptions,
    // Not under LEAD_MANAGEMENT_TABLE_QUERY_KEY — the optimistic updaters above prefix-match it
    // and iterate InfiniteData `pages`, which this plain-DTO entry lacks. Tags still invalidate it.
    queryKey: [LEAD_BY_CONVERSATION_LOOKUP_QUERY_KEY, postData] as const,
    queryFn: () => SatelliteWebLandlordLeadManagementHandler_leadManagementTable(postData),
    enabled: enabled && !!conversationId,
    staleTime: 30000,
    // Same endpoint, same retry policy as the table queries: transient failures retry, 4xx never
    // (a 403/404 for a deep-linked conversation can't be fixed by retrying or focus-refetching).
    retry: shouldRetryLeadManagementQuery,
    refetchOnWindowFocus: false,
    select: useCallback(
      (data: SatelliteWebLandlordLeadManagementTableResponseDto) =>
        data.leads.find((lead) => lead.conversationId === conversationId) ?? null,
      [conversationId],
    ),
    meta: {
      tags: ['leadManagement:table'],
      context: {
        api: 'leadManagementTable',
        details: 'useLeadLookupByConversationId - Error resolving lead by conversationId',
      },
    },
  });

  return lead ?? null;
}

/**
 * Look up a single lead by conversationId from the lead management table query cache, falling
 * back to the conversation-scoped lookup when the table has settled without the lead — e.g. the
 * open conversation is excluded by the active filters or sits beyond the loaded pages.
 * Uses `select` to only re-render when the specific lead changes, not when other leads update.
 * Must be rendered within the LeadManagementContext provider.
 */
export function useCachedLeadByConversationId(
  conversationId: string,
): SatelliteWebLandlordLeadManagementTableRowResponseDto | null {
  const {
    clientTimeZone,
    conversationIdToFilterBy,
    folder,
    listingAliases,
    minCriteriaMatches,
    readRepliedStatus,
    renterProgressStatus,
    source,
  } = useContext(LeadManagementContext);

  const params = useMemo(
    () => ({
      clientTimeZone,
      conversationId: conversationIdToFilterBy,
      listingAliases,
      folder,
      minCriteriaMatches,
      readRepliedStatus,
      renterProgressStatus,
      source,
    }),
    [
      clientTimeZone,
      conversationIdToFilterBy,
      folder,
      listingAliases,
      minCriteriaMatches,
      readRepliedStatus,
      renterProgressStatus,
      source,
    ],
  );

  const { data: cachedLead } = useInfiniteQuery({
    ...getInfiniteQueryOptions(params),
    refetchOnMount: false,
    select: useCallback(
      (data: LeadManagementInfiniteData) =>
        data.pages.flatMap((page) => page.leads ?? []).find((lead) => lead?.conversationId === conversationId) ?? null,
      [conversationId],
    ),
  });

  // `null` (as opposed to `undefined` while the table query has no data yet) means the table
  // settled and the lead is genuinely absent — only then is the network fallback warranted.
  const lookupLead = useLeadLookupByConversationId(conversationId, clientTimeZone, {
    enabled: cachedLead === null,
  });

  return cachedLead ?? lookupLead;
}
